import { saveQuoteStepDataApi } from '../../../apis/quotes';

// Step-wise quote storage (Motor TT first): each form that declares a `stepDataKey`
// has its field values saved onto the quote (formSteps.<stepDataKey>) when the user
// moves past it. The quote is identified by the policyToken issued on Declaration
// (see saveStepData for the earlier steps).

// Components arrive either formatted ({ name }) or raw with a slug prefix ({ Slug_name }).
const getComponentName = component => {
  if (!component || typeof component !== 'object') return null;
  if (typeof component.name === 'string') return component.name;
  const nameKey = Object.keys(component).find(key => key.endsWith('_name'));
  return nameKey && typeof component[nameKey] === 'string' ? component[nameKey] : null;
};

// Collects field names on a form, including fields inside option sub-forms
// (e.g. the mailing address fields under a checkbox option).
const collectComponentNames = (components, names = new Set()) => {
  (components || []).forEach(component => {
    const name = getComponentName(component);
    if (name) names.add(name);
    Object.values(component || {}).forEach(value => {
      if (!Array.isArray(value)) return;
      value.forEach(option => {
        const subForm = option?.optionSubForm?.form || option?.optionSubForm;
        if (subForm?.components) collectComponentNames(subForm.components, names);
      });
    });
  });
  return names;
};

export const buildStepData = (form, wizardData) => {
  const stepData = {};
  collectComponentNames(form?.components).forEach(name => {
    [name, `${name}Label`].forEach(key => {
      if (wizardData?.[key] !== undefined) stepData[key] = wizardData[key];
    });
  });
  return stepData;
};

// Tokens whose earlier steps have already been saved in this session.
const backfilledTokens = new Set();

const saveForm = (state, form, slug, policyToken) => {
  const wizardData = state?.wizards?.allInitialWizardData || {};
  return saveQuoteStepDataApi
    .saveQuoteStepData(state, {
      policyToken: String(policyToken),
      stepKey: form?.stepDataKey,
      stepIndex: form?.positionNo,
      productName: wizardData?.insurance || slug,
      stepData: buildStepData(form?.form, wizardData),
    })
    .catch(() => null);
};

// Fire-and-forget: a failed save must never block the user moving through the wizard.
// The token is issued after the earlier steps (Motor TT: on Declaration, once the
// vehicle body type and cover type that pick the policy prefix are known), so
// the first save with a new token also saves the steps before it.
export const saveStepData = ({ state, currentForm, slug, forms }) => {
  const policyToken = state?.wizards?.allInitialWizardData?.policyToken;
  if (!currentForm?.stepDataKey || !policyToken) return Promise.resolve(null);

  let formsToSave = [currentForm];
  if (!backfilledTokens.has(String(policyToken))) {
    backfilledTokens.add(String(policyToken));
    const earlierForms = (forms || []).filter(
      form => form?.stepDataKey && form?.positionNo < currentForm?.positionNo,
    );
    formsToSave = [...earlierForms, currentForm];
  }

  return Promise.all(formsToSave.map(form => saveForm(state, form, slug, policyToken)));
};
